import { forwardRef } from 'react';
import { NumericFormat, NumericFormatProps, OnValueChange } from 'react-number-format';
import Input, { InputProps } from './Input';

export interface CurrencyInputProps
  extends Omit<InputProps, 'type' | 'value' | 'onChange' | 'onlyNumbers'> {
  value: number | null | undefined;
  onValueChange: (value: number | null) => void;
  decimalScale?: number;
  allowNegative?: boolean;
}

const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ value, onValueChange, decimalScale = 2, allowNegative = false, ...inputProps }, ref) => {
    const handleValueChange: OnValueChange = (values, sourceInfo) => {
      if (sourceInfo.source !== 'event') return;
      onValueChange(values.floatValue ?? null);
    };

    const handlePaste: NumericFormatProps['onPaste'] = (e) => {
      const pasted = e.clipboardData.getData('text/plain');
      if (isUsFormat(pasted)) {
        e.preventDefault();
        const normalized = parseUsToNumber(pasted);
        if (normalized !== null) {
          onValueChange(normalized);
        }
      }
    };

    const displayValue = value === null || value === undefined ? '' : value;

    return (
      <NumericFormat
        {...inputProps}
        value={displayValue}
        onValueChange={handleValueChange}
        onPaste={handlePaste}
        thousandSeparator="."
        decimalSeparator=","
        decimalScale={decimalScale}
        fixedDecimalScale={false}
        allowNegative={allowNegative}
        allowLeadingZeros={false}
        customInput={Input}
        getInputRef={ref}
      />
    );
  }
);

CurrencyInput.displayName = 'CurrencyInput';

function isUsFormat(text: string): boolean {
  const trimmed = text.trim();
  const usPattern = /^\d{1,3}(,\d{3})+(\.\d+)?$/;
  return usPattern.test(trimmed);
}

function parseUsToNumber(text: string): number | null {
  const cleaned = text.trim().replace(/,/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

export default CurrencyInput;
